import React, { useState, useEffect } from 'react';
import { Mail, Check, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { EmailResendLimiter, maskEmail } from '../../config/confirmationConfig';

const EmailConfirmation = ({ email, bookingRef, onResend }) => {
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Check cooldown on mount
  useEffect(() => {
    const remaining = EmailResendLimiter.getCooldownRemaining(bookingRef);
    setCooldown(remaining);
  }, [bookingRef]);

  // Countdown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (!EmailResendLimiter.canResend(bookingRef)) {
      const remaining = EmailResendLimiter.getCooldownRemaining(bookingRef);
      setCooldown(remaining);
      return;
    }

    setIsResending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mark as resent and start cooldown
    EmailResendLimiter.markResent(bookingRef);
    setCooldown(60);
    setIsResending(false);
    setResent(true);
    
    // Fire analytics
    onResend();

    // Reset resent status after 3 seconds
    setTimeout(() => setResent(false), 3000);
  };

  const maskedEmail = maskEmail(email);

  return (
    <div className="bg-white rounded-xl p-5 flex items-center justify-between" data-testid="email-confirmation">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
          <Mail className="h-5 w-5 text-gray-600" />
        </div>
        <div>
          <p className="text-sm text-gray-600">Confirmation sent to</p>
          <p className="text-sm font-medium text-black">{maskedEmail}</p>
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleResend}
        disabled={isResending || cooldown > 0}
        className="text-[#FF8C00] hover:text-[#E67E00] hover:bg-[#FF8C00]/10"
        data-testid="resend-email-btn"
      >
        {isResending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : resent ? (
          <span className="flex items-center gap-1 text-green-600">
            <Check className="h-4 w-4" />
            Sent
          </span>
        ) : cooldown > 0 ? (
          <span className="text-gray-400">
            Resend in {cooldown}s
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <RefreshCw className="h-4 w-4" />
            Resend
          </span>
        )}
      </Button>
    </div>
  );
};

export default EmailConfirmation;
